import { lazy } from 'react';

// Add each new <domain>.App.js here so App.js can resolve the domain-specific shell.
const domainAppRegistry = {
  default: lazy(() => import('./default.App')),
  sarya: lazy(() => import('./sarya.App')),
};

const domainHostAliases = {
  sarya: 'sarya',
  'sarya.com': 'sarya',
  'www.sarya.com': 'sarya',
};

const normalizeHostValue = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/^https?:\/\//, '')
  .replace(/^www\./, '')
  .replace(/\/.*$/, '')
  .replace(/:\d+$/, '');

const currentNormalizedHost = (hostname) => normalizeHostValue(
  hostname || (typeof window !== 'undefined' ? window.location.hostname : '')
);

const pageDataMatchesHost = (pageData, hostname) => {
  const normalizedHost = currentNormalizedHost(hostname);
  if (!normalizedHost) {
    return true;
  }

  const firstHostLabel = normalizedHost.split('.')[0];
  const hostValues = [
    pageData?.domainUrl,
    pageData?.url,
    pageData?.host,
    pageData?.website,
    pageData?.primaryUrl,
    pageData?.id,
    pageData?.name,
    pageData?.firm,
    pageData?.firmId,
  ].flatMap((value) => Array.isArray(value) ? value : [value]);

  return hostValues.some((value) => {
    const normalizedValue = normalizeHostValue(value);
    return normalizedValue === normalizedHost || normalizedValue === firstHostLabel;
  });
};

const domainKeyFromHost = (hostname) => {
  const normalizedHost = normalizeHostValue(hostname);
  if (!normalizedHost) {
    return '';
  }

  if (domainHostAliases[normalizedHost]) {
    return domainHostAliases[normalizedHost];
  }

  const hostParts = normalizedHost.split('.').filter(Boolean);
  const firstLabel = hostParts[0];
  return domainHostAliases[firstLabel] || firstLabel || '';
};

const domainKeyFromPageData = (hostname) => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    const persistedHost = normalizeHostValue(window.localStorage.getItem('pageDataHost'));
    const normalizedHost = currentNormalizedHost(hostname);
    if (persistedHost && normalizedHost && persistedHost !== normalizedHost) {
      return '';
    }

    const rawPageData = window.localStorage.getItem('pageData');
    if (!rawPageData) {
      return '';
    }

    const parsedPageData = JSON.parse(rawPageData);
    if (!pageDataMatchesHost(parsedPageData, hostname)) {
      return '';
    }

    return String(
      parsedPageData?.domainAppKey
      || parsedPageData?.domainKey
      || parsedPageData?.domainName
      || parsedPageData?.businessName
      || ''
    ).trim().toLowerCase();
  } catch (error) {
    return '';
  }
};

export const resolveDomainAppKey = (hostname) => {
  const keyFromPageData = domainKeyFromPageData(hostname);
  if (keyFromPageData && domainAppRegistry[keyFromPageData]) {
    return keyFromPageData;
  }

  const keyFromHost = domainKeyFromHost(hostname || (typeof window !== 'undefined' ? window.location.hostname : ''));
  if (keyFromHost && domainAppRegistry[keyFromHost]) {
    return keyFromHost;
  }

  return 'default';
};

export const resolveDomainApp = (hostname) => (
  domainAppRegistry[resolveDomainAppKey(hostname)] || domainAppRegistry.default
);

export {
  domainAppRegistry,
};
